import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

/**
 * Custom React hook to manage fullscreen mode.
 *
 * Supports:
 *  - Entering fullscreen (`enterFullscreen`)
 *  - Exiting fullscreen (`exitFullscreen`)
 *  - Toggling fullscreen (`toggleFullscreen`)
 *  - Tracking fullscreen state (`isFullscreen`)
 *
 * Works with modern browsers and provides vendor-prefixed fallbacks.
 */
export function useFullscreen<T extends HTMLElement>(ref: RefObject<T>) {
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Helper: detect the current fullscreen element (cross-browser)
    const getFsElement = () =>
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;

    /**
     * Enter fullscreen mode.
     * Uses vendor-prefixed APIs for Safari, Firefox, and IE.
     */
    const enterFullscreen = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        if (el.requestFullscreen) el.requestFullscreen();
        else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
        else if ((el as any).mozRequestFullScreen) (el as any).mozRequestFullScreen();
        else if ((el as any).msRequestFullscreen) (el as any).msRequestFullscreen();

        // Optional: Auto-focus for keyboard navigation support
        setTimeout(() => el.focus(), 50);
    }, [ref]);

    /**
     * Exit fullscreen mode.
     * Uses vendor-prefixed APIs for Safari, Firefox, and IE.
     */
    const exitFullscreen = useCallback(() => {
        if (document.exitFullscreen) document.exitFullscreen();
        else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
        else if ((document as any).mozCancelFullScreen) (document as any).mozCancelFullScreen();
        else if ((document as any).msExitFullscreen) (document as any).msExitFullscreen();
    }, []);

    /**
     * Toggle fullscreen:
     * - If already in fullscreen → exit.
     * - Otherwise → enter fullscreen.
     */
    const toggleFullscreen = useCallback(() => {
        if (getFsElement()) {
            exitFullscreen();
        } else {
            enterFullscreen();
        }
    }, [enterFullscreen, exitFullscreen]);

    /**
     * Handle ESC key manually to improve reliability:
     *
     * Problem:
     *   In Chrome, when DevTools are open and docked, the `fullscreenchange`
     *   event may be delayed or swallowed. Additionally, the first ESC `keydown`
     *   event can sometimes be consumed by the browser and not propagate to the app.
     *
     * Solution:
     *   - We listen to the ESC key and force `exitFullscreen()` ourselves.
     *   - We use a short `setTimeout` to re-sync `isFullscreen` because
     *     `document.fullscreenElement` may not update immediately after ESC.
     *
     * Why not rely only on `fullscreenchange`:
     *   - Normally, the browser fires `fullscreenchange` when exiting fullscreen.
     *   - But with DevTools docked, the event can fire late or inconsistently.
     *   - By explicitly handling ESC, we guarantee consistent behavior across browsers.
     *
     *   so we need to click the ESC key to exit fullscreen 2 times
     **/
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (document.fullscreenElement) {
                    exitFullscreen();
                }
                // Always sync after Chrome updates
                setTimeout(() => {
                    setIsFullscreen(!!document.fullscreenElement);
                }, 50);
            }
        };

        document.addEventListener('keyup', handleEsc);
        return () => document.removeEventListener('keyup', handleEsc);
    }, [exitFullscreen]);

    /**
     * Listen for fullscreen changes:
     * Ensures `isFullscreen` stays in sync when the user:
     *  - Presses ESC
     *  - Uses browser UI to exit fullscreen
     */
    useEffect(() => {
        const handleChange = () => setIsFullscreen(!!getFsElement());

        document.addEventListener('fullscreenchange', handleChange);
        document.addEventListener('webkitfullscreenchange', handleChange);
        document.addEventListener('mozfullscreenchange', handleChange);
        document.addEventListener('MSFullscreenChange', handleChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleChange);
            document.removeEventListener('webkitfullscreenchange', handleChange);
            document.removeEventListener('mozfullscreenchange', handleChange);
            document.removeEventListener('MSFullscreenChange', handleChange);
        };
    }, []);

    return { isFullscreen, enterFullscreen, exitFullscreen, toggleFullscreen };
}
